import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { HOURGLASS_SERVICE } from "@cervval.js/client-html5-core/src/services/hourglass.service";
import { DarkDivHourGlassService } from "@cervval.js/client-html5-core/src/services/hourglass/darkDiv.hourglass.service";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { CjsGeneratedForm } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm";
import { GeneratedFormEditMode, GeneratedFormSaveMode } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm.types";
import { ToastStyle } from "@cervval.js/client-html5-ui/src/components/toast/toast";
import { TOAST_SERVICE } from "@cervval.js/client-html5-ui/src/components/toast/toastService/toast.service";
import { DefaultToastService } from "@cervval.js/client-html5-ui/src/components/toast/toastService/toastService.impl/default.toast.service";
import { CjsEditor } from "@cervval.js/client-html5-ui/src/editors/editor";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DataModel } from "@cervval.js/common/src/forms/dataModel";
import { anyReallyAny } from "@cervval.js/common/src/tools/object.tools";
import { CLIENT_GOOGLE_RECAPTCHA_SERVICE } from "@ehealth_tools/client-google-recaptcha/src/services/clientGoogleRecaptcha.service";
import { translateHTMLReferences } from "@ehealth_tools/client-html-tools/src/tools/webcomponent.tools";
import { CgvCheckbox } from "@sportsante/client-shared/src/components/cgvComponents/cgvCheckbox/cgvCheckbox";
import { exportPatientDTOToPropertyConfig } from "@sportsante/client-shared/src/propertyConfigs/client.patient.propertyConfig";
import { CheckUidResult } from "@sportsante/common-shared/src/data/users/user.definitions";
import { PatientDTO } from "@sportsante/common-shared/src/data/users/user.dto";
import { PATIENT_REGISTRATION_RETURN_STATUS, RegistrationRoutes } from "@sportsante/common-shared/src/routes/registration.routes";
import { ActivmedBookingCaptcha } from "../../../core/services/booking.googleReCaptcha.service";
import { I18nPatientRegistration } from "./patientRegistrationForm.i18n";

@CustomElement({
    name: "patient-registration-form",
    template: require("./patientRegistrationForm.html"),
    style: require("./patientRegistrationForm.scss"),
    elementCssClasses: [W_100, H_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, PADDING.P_2],
    i18n: require("./patientRegistrationForm.i18n").I18n
})
export class PatientRegistrationForm<I18n extends I18nPatientRegistration = I18nPatientRegistration> extends WebComponent<I18n> {
    @TypedHtmlElements({ ensureLoadedClasses: [CjsGeneratedForm, CjsButton, CgvCheckbox] })
    public readonly components!: {
        form: CjsGeneratedForm<PatientDTO>,
        saveBtn: CjsButton,
        cgvCheckbox: CgvCheckbox,
        instructionText: HTMLParagraphElement
    };

    @TypedClientAjaxProxy()
    private readonly _registrationRoutes!: RegistrationRoutes<ClientRouteOptions>;

    @Injected(HOURGLASS_SERVICE)
    private readonly darkDivService!: DarkDivHourGlassService;
    @Injected(TOAST_SERVICE)
    private readonly toastService!: DefaultToastService;
    @Injected(CLIENT_GOOGLE_RECAPTCHA_SERVICE)
    private readonly _captchaService!: ActivmedBookingCaptcha;

    private _patientDataModel: DataModel<PatientDTO> = new DataModel("PatientRegistrationForm");

    /**
     * Event to trigger on patient save
     */
    @Event(NoBubblesEventInit)
    public saveEvent!: EventHandler;

    /** Init component */
    /** @inheritdoc */
    public override init(): void {
        this._initForms();
        this._initButtons();
    }

    /**
     * Init buttons of the component and bind interactions
     */
    private _initButtons(): void {
        // Set save button
        this.components.saveBtn.options.setAndRefreshIfChanged({
            label: this.i18n.saveBtn.label,
            iconMode: "before",
            icon: {
                name: "fa-floppy-disk"
            },
            cssStyle: ButtonStyle.SECONDARY
        });

        this.components.saveBtn.clickEvent.on(() => {
            void this._save().catch((err) => {
                showErrorInDialog(err);
            });
        });
    }

    /** @inheritdoc */
    public override refresh(): void {
        translateHTMLReferences(this.components, this.i18n.html);
        this.resetDataToEdit();
    }

    /**
     * Set data to edit in form.
     */
    public resetDataToEdit(): void {
        this._patientDataModel.setData([]);
        this.components.form.initialize(
            exportPatientDTOToPropertyConfig(),
            this._patientDataModel, this._patientDataModel.getDataCopy());
    }

    /** Init component form */
    private _initForms(): void {
        this.components.form.options.setWithoutRefresh({
            formMode: { editMode: GeneratedFormEditMode.CREATE, saveMode: GeneratedFormSaveMode.STATIC }
        });
    }

    /** Validate form for coach registration */
    private async _validateForm(): Promise<boolean> {
        try {
            const formValid = this.components.form.validateForm();
            if (!formValid) { return false; }
            const emailEditor = this.components.form.getFormItems()["email"].editor;
            const uidInput = emailEditor.getValue() as string ?? "";
            if (uidInput.length > 0) {
                const checkUid = await this._captchaService.encapsulateRouteCallWithCaptcha(this._registrationRoutes.isUserMailAvailable, { email: uidInput });
                if (checkUid === CheckUidResult.SUCCESS) { return true; }
                this._showCheckUidErrorInEditor(checkUid, emailEditor);
                return false;
            }
            this._showCheckUidErrorInEditor(CheckUidResult.UNREACHABLE, emailEditor);
            return false;
        } catch (err) {
            showErrorInDialog(err);
            return false;
        }
    }

    /**
     * Display an error toast when patient registration went wrong
     * @param code CheckUidResult code
     */
    private _showCheckUidErrorInEditor(code: CheckUidResult, editor: CjsEditor<anyReallyAny>): void {
        let content = "";
        switch (code) {
            case CheckUidResult.UNREACHABLE:
                content = this.i18n.errorEmail;
                break;
            case CheckUidResult.USER_EXIST:
                content = this.i18n.errorUser;
                break;
            case CheckUidResult.SUCCESS:
                break;
            case CheckUidResult.UNKNOWN_ERROR:
            default:
                content = this.i18n.errorUnknown;
                break;
        }
        if (content.length > 0) { editor.setValidationError({ errorMessage: content }); }
    }

    /**
     * Save function to save a coach profile
     * @returns 
     */
    private async _save(): Promise<void> {
        const a = this.darkDivService.show();
        try {
            const formValid = await this._validateForm();
            if (!formValid) {
                return;
            }

            const cgvCheck = this.components.cgvCheckbox.getValue();
            if (cgvCheck !== true) {
                this.components.cgvCheckbox.setCgvValidationError();
                return;
            }

            // Retrieve actions
            const toDoActionsForm = this.components.form.save().elementsToCreate;

            if (!toDoActionsForm.length) { throw Error("No elements to save"); }
            const patientToPush = toDoActionsForm[0];

            const creationResult = await this._captchaService.encapsulateRouteCallWithCaptcha(this._registrationRoutes.registerPatientWithoutAffectation, patientToPush);

            if (creationResult === PATIENT_REGISTRATION_RETURN_STATUS.SUCCESS) {
                this.saveEvent.emit();
            } else {
                this.toastService.show({
                    enableDismiss: true,
                    title: this.i18n.saveUnknownError,
                    delay: 3000,
                    cssStyle: ToastStyle.DANGER
                });
            }


        }
        catch (err) {
            showErrorInDialog(err);
        }
        finally {
            this.darkDivService.hide(a);
        }
    }
}